/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import { useToggle } from '@hooks/useToggle';
import { MessageFormProps, MessageFormField } from '../types';
import { DestinationCheckboxFields } from './DestinationCheckboxFields';
import { MessageField } from './MessageField';
import { QuestionsFilter } from './QuestionsFilter';
import { SubjectField } from './SubjectField';
import { UserSelectField } from './UserSelectField';
import { getFilterOptions } from './QuestionsOptions';
import { SystemUsersToggle } from './SystemUsersToggle';
import { DelegationSource, getDelegationSource, getFilteredUsers, getSystemHint } from '../utils';
import { QuestionStatusStats, UserDelegationStats } from '../../../types/initiativeStats';

interface BulkUsersMessageFormProps extends MessageFormProps {
  users: UserDelegationStats[];
}

export const BulkUsersMessageForm = (props: BulkUsersMessageFormProps) => {
  const { onChange, form, users } = props;
  const [selectedStatus, setSelectedStatus] = useState<QuestionStatusStats | undefined>(undefined);
  const [showSystemUsers, toggleShowSystemUsers] = useToggle(false);
  const source = getDelegationSource(showSystemUsers);

  const selectRecipients = (value: QuestionStatusStats, nextSource: DelegationSource) => {
    onChange(MessageFormField.UserIds)(getFilteredUsers(users, value, nextSource).map((user) => user._id));
  };

  const onChangeQuestionsFilter = (value: QuestionStatusStats) => {
    if (value === selectedStatus) {
      return;
    }
    setSelectedStatus(value);
    selectRecipients(value, source);
  };

  const onToggleSystemUsers = () => {
    toggleShowSystemUsers();
    if (selectedStatus) {
      selectRecipients(selectedStatus, getDelegationSource(!showSystemUsers));
    }
  };

  const filteredUsers = selectedStatus
    ? getFilteredUsers(users, selectedStatus, source).map((user) => ({
        ...user,
        hint: showSystemUsers ? getSystemHint(user) : undefined,
      }))
    : [];

  return (
    <div className='d-flex flex-column'>
      <div className='row'>
        <div className='col-7'>
          <QuestionsFilter label='Message users with' options={getFilterOptions} onChange={onChangeQuestionsFilter} />
        </div>
        <div className='col-5 d-flex align-items-end justify-content-end pb-1'>
          <SystemUsersToggle checked={showSystemUsers} onChange={onToggleSystemUsers} />
        </div>
      </div>
      <UserSelectField users={filteredUsers} userIds={form.userIds} onChange={onChange(MessageFormField.UserIds)} />
      <SubjectField subject={form.subject} onChange={onChange(MessageFormField.Subject)} />
      <MessageField message={form.message} onChange={onChange(MessageFormField.Message)} />
      <DestinationCheckboxFields sendByEmail={form.sendByEmail} onChange={onChange(MessageFormField.SendByEmail)} />
    </div>
  );
}
